/**
 * CreateChronicleModal — DEV-1336 (#2 creation flow + "+New = shelves" redesign).
 *
 * "What you can create" now equals "what you browse": the picker shows the same
 * content-template SHELVES as the bookshelf (via the shared TemplateSelector),
 * instead of the old divergent 4-group/10-type chronicle taxonomy.
 *
 * Scope (Skip 2026-06-17): CHRONICLE shelves first. Each chronicle shelf maps to
 * a single stored categoryKey (sub-types collapsed). The non-chronicle shelves
 * (Mini-Project, News, Scholarly, Best Practice) are routed to their own create
 * flows in a fast-follow, so they are filtered out here for now.
 *
 * Flow: pick shelf → name it → create a DRAFT → hand the GUID back so the caller
 * navigates to the detail page (no legacy full-page editor).
 */

import { useState } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import TemplateSelector from '../TemplateSelector/TemplateSelector';
import { createChronicle } from '../../api/chronicles';
import type { ContentTemplateDefinition, ContentTemplateKey } from '../../Data/Content/contentTemplates';

/** Chronicle-backed shelves → the categoryKey stored on the chronicle (sub-types collapsed). */
const CHRONICLE_SHELF_CATEGORY: Partial<Record<ContentTemplateKey, string>> = {
  // DEV-1381. Present here so the Blog shelf is creatable from the /content "+"
  // flow too (SPEC §12.5 — the author's own bookshelf). The profile Blog tab does
  // NOT come through this modal: it skips the type picker entirely (single-type
  // surface, SPEC §12.3) and calls createJournalEntry() directly.
  journal_entry: 'journal_entry',
  project_chronicle: 'project',
  how_to: 'how_to',
  topic_tracker: 'topic_tracker',
  lesson_learned: 'learning',
};

interface Props {
  /** Unlocked content templates for this user (trust-gated), from the Content page. */
  templates: ContentTemplateDefinition[];
  onClose: () => void;
  /** Called with the new chronicle GUID after a successful create. */
  onCreated: (chronicleGuid: string) => void;
}

export default function CreateChronicleModal({ templates, onClose, onCreated }: Props) {
  const { t } = useAppTranslation('pages');

  const [step, setStep] = useState<'type' | 'name'>('type');
  const [categoryKey, setCategoryKey] = useState('');
  // The selected content-type key (DEV-1392). Sent on create so the server owns
  // the reviewModel bar; we never send reviewModel from the client.
  const [templateKey, setTemplateKey] = useState<ContentTemplateKey | ''>('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Chronicle shelves only (fast-follow adds the rest), trust-gated by the caller.
  const chronicleShelves = templates.filter((tmpl) => CHRONICLE_SHELF_CATEGORY[tmpl.key]);

  const handleCreate = async () => {
    if (!title.trim()) {
      setError(t('chronicleCreate.errorTitle'));
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const created = await createChronicle({ title: title.trim(), categoryKey, templateKey });
      onCreated(created.chronicleGuid);
    } catch (e: any) {
      setError(e?.message || 'Create failed');
      setCreating(false);
    }
  };

  // Step 1 — pick a shelf (same shelves as the bookshelf).
  if (step === 'type') {
    return (
      <TemplateSelector
        templates={chronicleShelves}
        onSelect={(key) => {
          const cat = CHRONICLE_SHELF_CATEGORY[key];
          if (!cat) return;
          setCategoryKey(cat);
          setTemplateKey(key);
          setStep('name');
        }}
        onClose={onClose}
      />
    );
  }

  // Step 2 — name the chronicle, then create the draft.
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
  };
  const card: React.CSSProperties = {
    width: 'min(460px, 100%)', borderRadius: 12, border: '1px solid var(--border-default)',
    backgroundColor: 'var(--bg-primary)', backgroundImage: 'linear-gradient(var(--bg-elevated), var(--bg-elevated))',
    boxShadow: 'var(--shadow-lg)',
  };
  const fieldStyle: React.CSSProperties = {
    width: '100%', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--border-default)',
    background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: 14,
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '16px 18px', borderBlockEnd: '1px solid var(--border-subtle)' }}>
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--text-primary)' }}>{t('chronicleCreate.nameHeading')}</h2>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 13, color: 'var(--text-tertiary)' }} htmlFor="cc-title">{t('chronicleCreate.titleLabel')}</label>
          <input
            id="cc-title"
            autoFocus
            style={fieldStyle}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder={t('chronicleCreate.titlePlaceholder')}
          />
          {error && <div style={{ color: 'var(--color-error)', fontSize: 13 }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '14px 18px', borderBlockStart: '1px solid var(--border-subtle)' }}>
          <button type="button" onClick={() => setStep('type')} disabled={creating} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontWeight: 600 }}>{t('chronicleCreate.back')}</button>
          <button type="button" onClick={handleCreate} disabled={creating} style={{ padding: '8px 16px', borderRadius: 8, border: 0, background: 'var(--accent-primary)', color: '#fff', cursor: creating ? 'wait' : 'pointer', fontWeight: 600 }}>{creating ? t('chronicleCreate.creating') : t('chronicleCreate.create')}</button>
        </div>
      </div>
    </div>
  );
}
